/**
 * The Roadmap page's logic, outside React: statuses and priorities, the
 * default rank, the groups each month shows and who sees and changes its
 * budget, the column sort, the filters and the month in the URL, the
 * blockages' lines, the followers' avatars and the month's figures.
 *
 * Each helper reads only the fields it needs, through a small structural
 * interface, so it takes the API's roadmap items as they are.
 */

import type {
  RoadmapBlockerCreate,
  RoadmapBlockerKind,
  RoadmapDeveloperUpdate,
  RoadmapIdeaProposal,
  RoadmapItemCreate,
  RoadmapItemUpdate,
  RoadmapPermissions,
  RoadmapStatusChange,
} from "@/lib/api";
import { apiErrorCode, apiErrorMessage } from "@/lib/api-errors";
import { isMonth, shiftMonth } from "@/lib/calendar";
import type { ChipTone } from "@/lib/chips";
import { daysLabel } from "@/lib/effort";
import {
  belgianDay,
  formatList,
  formatMonth,
  formatMonthName,
  formatNumber,
  LOCALES,
} from "@/lib/format";
import { i18n } from "@/lib/i18n";
import { getLanguage, type Language } from "@/lib/i18n/language";
import { firstNameLabel, type NamedPerson, normalEmail, personInitials } from "@/lib/people";
import { roleLabel } from "@/lib/roles";
import { matchesSearch } from "@/lib/search";

// ─── Statuses and priorities ─────────────────────────────────────────────

/** An item's statuses, in the order work goes through them. */
export const ROADMAP_STATUSES = ["idea", "planned", "in_progress", "done"] as const;
export type RoadmapStatus = (typeof ROADMAP_STATUSES)[number];

/**
 * The table's groups, in the page's order: what is done first (folded by
 * default), then what is in progress, what is planned and the ideas.
 */
export const STATUS_GROUPS: readonly RoadmapStatus[] = ["done", "in_progress", "planned", "idea"];

/** The priorities, the highest first. */
export const ROADMAP_PRIORITIES = ["high", "medium", "low"] as const;
export type RoadmapPriority = (typeof ROADMAP_PRIORITIES)[number];

/** A priority's chip: high in solid De Ceuster blue, medium in its light tint, low in grey. */
export const PRIORITY_TONE: Record<string, ChipTone> = {
  high: "write",
  medium: "read",
  low: "neutral",
};

export function isRoadmapStatus(value: unknown): value is RoadmapStatus {
  return typeof value === "string" && (ROADMAP_STATUSES as readonly string[]).includes(value);
}

export function isRoadmapPriority(value: unknown): value is RoadmapPriority {
  return typeof value === "string" && (ROADMAP_PRIORITIES as readonly string[]).includes(value);
}

/** A status in the dashboard's language ("Bezig"); one it does not know as it came. */
export function statusLabel(status: string): string {
  return isRoadmapStatus(status) ? i18n.t(`roadmap.status.${status}`) : status;
}

/** A priority in the dashboard's language ("Hoog"); one it does not know as it came. */
export function priorityLabel(priority: string): string {
  return isRoadmapPriority(priority) ? i18n.t(`roadmap.priority.${priority}`) : priority;
}

/** A priority's place: 0 for high, 2 for low, after them for one the page does not know. */
function priorityRank(priority: string): number {
  const index = (ROADMAP_PRIORITIES as readonly string[]).indexOf(priority);
  return index < 0 ? ROADMAP_PRIORITIES.length : index;
}

// ─── The default rank ────────────────────────────────────────────────────

/** A follower's weight in the rank, by their dashboard role. */
const ROLE_WEIGHTS: ReadonlyMap<string, number> = new Map([
  ["owner", 3],
  ["admin", 2],
]);

/**
 * How much a follower weighs in the rank: an owner 3, an admin 2, anyone
 * else (a developer, a member, a role the page does not know) 1.
 */
export function roleWeight(role: string | null | undefined): number {
  return (role ? ROLE_WEIGHTS.get(role) : undefined) ?? 1;
}

/** Every dashboard account's role, by email in lower case. */
export function rolesByEmail(
  people: readonly { email: string; role: string }[],
): Map<string, string> {
  return new Map(people.map((person) => [normalEmail(person.email), person.role]));
}

/** The weight of an item's most senior follower (`roleWeight`); 0 without followers. */
export function seniorWeight(
  followers: readonly string[],
  roles: ReadonlyMap<string, string>,
): number {
  return followers.reduce(
    (most, email) => Math.max(most, roleWeight(roles.get(normalEmail(email)))),
    0,
  );
}

/** What the default rank reads of an item. */
export interface RankFields {
  id: string;
  status: string;
  priority: string;
  followers: readonly string[];
  created_at: string;
  done_at?: string | null;
}

/** A moment's time in ms; null when it is missing or no moment. */
function timeOf(value: string | null | undefined): number | null {
  const time = value ? Date.parse(value) : Number.NaN;
  return Number.isNaN(time) ? null : time;
}

function compareIds(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * The default order of two items of one status, as the server ranks them:
 * the higher priority first, then more followers, then the most senior
 * follower (`seniorWeight`), then the oldest, then by id. Done items go by
 * when they were done instead, the most recent first.
 */
export function compareRank(
  a: RankFields,
  b: RankFields,
  roles: ReadonlyMap<string, string>,
): number {
  if (a.status === "done" && b.status === "done") {
    const doneA = timeOf(a.done_at);
    const doneB = timeOf(b.done_at);
    if (doneA !== doneB) {
      // The most recent first; one without a time last.
      return doneA === null ? 1 : doneB === null ? -1 : doneB - doneA;
    }
    return compareIds(a.id, b.id);
  }
  const keys = [
    priorityRank(a.priority) - priorityRank(b.priority),
    b.followers.length - a.followers.length,
    seniorWeight(b.followers, roles) - seniorWeight(a.followers, roles),
  ];
  const decided = keys.find((key) => key !== 0);
  if (decided !== undefined) {
    return decided;
  }
  const createdA = timeOf(a.created_at);
  const createdB = timeOf(b.created_at);
  if (createdA !== createdB) {
    // The oldest first; one without a time last.
    return createdA === null ? 1 : createdB === null ? -1 : createdA - createdB;
  }
  return compareIds(a.id, b.id);
}

/**
 * Items in the default order: by status in the table's order
 * (`STATUS_GROUPS`), then by rank within each status (`compareRank`). The
 * server sends them so; this puts a list back in that order.
 */
export function rankItems<T extends RankFields>(
  items: readonly T[],
  roles: ReadonlyMap<string, string>,
): T[] {
  const group = (status: string) => {
    const index = (STATUS_GROUPS as readonly string[]).indexOf(status);
    return index < 0 ? STATUS_GROUPS.length : index;
  };
  return [...items].sort((a, b) => group(a.status) - group(b.status) || compareRank(a, b, roles));
}

/** A group of the table: a status and its items. */
export interface StatusGroup<T> {
  status: RoadmapStatus;
  items: T[];
}

// ─── The month on show ───────────────────────────────────────────────────

/** Where a month sits against the current month in Belgium: gone, now, or still to come. */
export type MonthKind = "past" | "current" | "later";

/** Where a month ("2026-10") sits against the current month ("2026-11"). */
export function monthKind(month: string, current: string): MonthKind {
  if (month < current) {
    return "past";
  }
  return month === current ? "current" : "later";
}

/**
 * The table's groups for each kind of month, in the page's order: a past
 * month shows only what was done in it; the current month what was done in
 * it, what is in progress, what is planned in it and the ideas; a later
 * month what is planned in it and the ideas.
 */
const MONTH_GROUPS: Record<MonthKind, readonly RoadmapStatus[]> = {
  past: ["done"],
  current: STATUS_GROUPS,
  later: ["planned", "idea"],
};

/** What the month's view reads of an item. */
export interface MonthViewFields {
  status: string;
  /** "2026-11"; null for an idea. */
  month?: string | null;
}

/**
 * Whether the table shows an item while a month ("2026-11") is on show,
 * against the current month: a done item in the month it counts against,
 * an item in progress in the current month, a planned item in its own month
 * only, and an idea in the current month and later ones. A past month thus
 * shows only what was done in it.
 *
 * The month is the server's: open work whose own month went by comes with
 * the current month, so a planned item that was never started shows in the
 * current month's Gepland and counts on its bar, as the month's figures have
 * it.
 */
export function inMonthView(item: MonthViewFields, month: string, current: string): boolean {
  const kind = monthKind(month, current);
  if (!isRoadmapStatus(item.status) || !MONTH_GROUPS[kind].includes(item.status)) {
    return false;
  }
  switch (item.status) {
    case "done":
    case "planned":
      return item.month === month;
    case "in_progress":
    case "idea":
      return true;
  }
}

/**
 * The table's groups while a month is on show (`MONTH_GROUPS`), each with
 * the items it shows of that month (`inMonthView`), also an empty one, each
 * keeping the order the items came in.
 */
export function monthStatusGroups<T extends MonthViewFields>(
  items: readonly T[],
  month: string,
  current: string,
): StatusGroup<T>[] {
  return MONTH_GROUPS[monthKind(month, current)].map((status) => ({
    status,
    items: items.filter((item) => item.status === status && inMonthView(item, month, current)),
  }));
}

/**
 * The month an idea is planned in from the table or the details: the month
 * on show, or the current month while a past month is on show.
 */
export function planMonthFor(month: string, current: string): string {
  return monthKind(month, current) === "past" ? current : month;
}

/** The month's budget and what is left of it, as an owner and a developer get them. */
export interface MonthBudget {
  budgetHours: number;
  /** Negative when the month holds more than its budget. */
  leftHours: number;
}

/**
 * The month's budget, for a viewer who sees it: an owner and a developer.
 * Null for an admin, whose month carries no budget and nothing left of it:
 * they see the month's progress alone.
 */
export function monthBudget(month: {
  budget_hours?: number | null;
  left_hours?: number | null;
}): MonthBudget | null {
  const budget = month.budget_hours;
  const left = month.left_hours;
  return typeof budget === "number" && typeof left === "number"
    ? { budgetHours: budget, leftHours: left }
    : null;
}

/** What a month's items used and still use, as an owner and a developer get them. */
export interface MonthProgress {
  /** What its done items used. */
  doneHours: number;
  /** What its planned items and items in progress use. */
  plannedHours: number;
}

/**
 * The month's progress in hours, for a viewer who sees the hours: an owner
 * and a developer. Null for an admin, whose month carries no hours: they see
 * its progress by the bar alone.
 */
export function monthProgress(month: {
  done_hours?: number | null;
  planned_hours?: number | null;
}): MonthProgress | null {
  const done = month.done_hours;
  const planned = month.planned_hours;
  return typeof done === "number" && typeof planned === "number"
    ? { doneHours: done, plannedHours: planned }
    : null;
}

/** What the budget's edit control reads of the viewer. */
export interface BudgetViewer {
  /** An owner or a developer (the server's `viewer.can_set_budget`). */
  can_set_budget: boolean;
  is_developer: boolean;
}

/**
 * Whether the viewer may change a month's budget, and whether the
 * development team hears of the change: a developer changes any month's,
 * quietly; an owner the current month's and later ones', and the team hears
 * of it. Null for anyone else, and for an owner on a past month.
 */
export function budgetEdit(
  viewer: BudgetViewer,
  month: string,
  current: string,
): { notifiesTeam: boolean } | null {
  if (!viewer.can_set_budget) {
    return null;
  }
  if (viewer.is_developer) {
    return { notifiesTeam: false };
  }
  return monthKind(month, current) === "past" ? null : { notifiesTeam: true };
}

// ─── Folding the groups ──────────────────────────────────────────────────

/** A group of the table that folds: a status's, or the triage a developer reviews. */
export type TableGroupKey = RoadmapStatus | "triage";

/** The groups folded when the page opens: Klaar, so the work ahead comes first. */
export const INITIALLY_FOLDED: ReadonlySet<TableGroupKey> = new Set(["done"]);

/** The folded groups after a click on a group's toggle: it folds, or unfolds when it was folded. */
export function toggleFolded(
  folded: ReadonlySet<TableGroupKey>,
  group: TableGroupKey,
): Set<TableGroupKey> {
  const next = new Set(folded);
  if (!next.delete(group)) {
    next.add(group);
  }
  return next;
}

/**
 * The folded groups, kept apart for browsing and for a search or filter: a
 * hit in Klaar, which starts folded, must show as a row, never as a count
 * in a folded group that reads like "nothing found".
 */
export interface GroupFolds {
  /** The groups folded while no search or filter is on. */
  browsing: ReadonlySet<TableGroupKey>;
  /** The groups folded while a search or filter is on; null while none is. */
  filtering: ReadonlySet<TableGroupKey> | null;
}

/** The folds when the page opens, before a search or filter: Klaar folded. */
export const INITIAL_FOLDS: GroupFolds = { browsing: INITIALLY_FOLDED, filtering: null };

/** The folds with every group open. */
const OPEN_FOLDS: GroupFolds = { browsing: new Set(), filtering: null };

/** The folds of each kind of month, kept apart: what is folded in one kind stays in the others. */
export type MonthFolds = Record<MonthKind, GroupFolds>;

/**
 * The folds when the page opens: in the current month Klaar starts folded,
 * so the work ahead comes first; a past month, which shows nothing but
 * Klaar, and a later month, which has no Klaar, start with every group open.
 */
export const INITIAL_MONTH_FOLDS: MonthFolds = {
  past: OPEN_FOLDS,
  current: INITIAL_FOLDS,
  later: OPEN_FOLDS,
};

/**
 * The folds for whether a search or filter is on: one that starts opens
 * every group, so each hit shows; one that ends brings back the folds from
 * before it. The same object when that changes nothing.
 */
export function foldsWhile(folds: GroupFolds, filtering: boolean): GroupFolds {
  if (filtering === (folds.filtering !== null)) {
    return folds;
  }
  return { browsing: folds.browsing, filtering: filtering ? new Set() : null };
}

/** The groups folded on screen. */
export function shownFolds(folds: GroupFolds): ReadonlySet<TableGroupKey> {
  return folds.filtering ?? folds.browsing;
}

/** The folds after a click on a group's toggle: in the search's folds while one is on. */
export function toggleGroupFold(folds: GroupFolds, group: TableGroupKey): GroupFolds {
  return folds.filtering
    ? { ...folds, filtering: toggleFolded(folds.filtering, group) }
    : { ...folds, browsing: toggleFolded(folds.browsing, group) };
}

/** An item a developer just moved from its row's status menu, and the status it moved to. */
export interface MovedItem {
  id: string;
  status: RoadmapStatus;
}

/**
 * Where the focus goes once a moved item is in its new group, as CSS
 * selectors to try in order: the item's status symbol in its new row, or,
 * with that group folded, the group's toggle. The row the focus was on left
 * the table with the move.
 */
export function movedFocusSelectors(moved: MovedItem): string[] {
  const id = moved.id.replace(/["\\]/g, "\\$&");
  return [
    `[data-status-of="${id}"]`,
    `tbody[data-group="${moved.status}"] th[scope="rowgroup"] button[aria-expanded]`,
  ];
}

// ─── Effort ──────────────────────────────────────────────────────────────

/** What an item's effort reads of it. */
export interface EffortFields {
  status: string;
  effort_hours?: number | null;
  used_hours?: number | null;
  actual_hours?: number | null;
}

/** An item's effort as the page shows it, and whether it is the actual hours or the estimate. */
export interface ItemEffort {
  /** Null while there is no estimate. */
  hours: number | null;
  actual: boolean;
}

/**
 * An item's effort: the shown estimate, or for a done item the hours it used
 * (its actual hours when they were set, else the shown estimate).
 */
export function itemEffort(item: EffortFields): ItemEffort {
  if (item.status === "done") {
    const actual = item.actual_hours ?? null;
    return actual !== null
      ? { hours: actual, actual: true }
      : { hours: item.used_hours ?? item.effort_hours ?? null, actual: false };
  }
  return { hours: item.effort_hours ?? null, actual: false };
}

/**
 * The hours of a group, for its header row: what its items count against
 * their months (`used_hours`), and for the ideas, which count against no
 * month, their estimates.
 */
export function groupHours(items: readonly EffortFields[]): number {
  return items.reduce(
    (sum, item) => sum + ((item.status === "idea" ? item.effort_hours : item.used_hours) ?? 0),
    0,
  );
}

/**
 * A group's header row's figures: "3 items, 1,5 dag", or "3 items" alone for
 * a viewer who does not see the hours (an admin, `seesRoadmapHours`).
 */
export function groupTotalLabel(items: readonly EffortFields[], showHours: boolean): string {
  const count = i18n.t("roadmap.itemCount", { count: items.length });
  return showHours
    ? i18n.t("roadmap.groupTotal", { items: count, effort: daysLabel(groupHours(items)) })
    : count;
}

// ─── Column sort ─────────────────────────────────────────────────────────

/** The table's columns, each of which sorts. */
export const ROADMAP_COLUMNS = [
  "item",
  "priority",
  "effort",
  "month",
  "deadline",
  "followers",
] as const;
export type RoadmapColumn = (typeof ROADMAP_COLUMNS)[number];

/**
 * The columns the viewer gets: every one for a viewer who sees the hours (an
 * owner and a developer), and for an admin every one but the effort.
 */
export function roadmapColumns(showHours: boolean): readonly RoadmapColumn[] {
  return showHours ? ROADMAP_COLUMNS : ROADMAP_COLUMNS.filter((column) => column !== "effort");
}

/**
 * The column sort the viewer gets: a sort on a column they do not have (the
 * effort, for an admin, from a shared link) keeps the default rank instead.
 */
export function shownSort(sort: RoadmapSort | null, showHours: boolean): RoadmapSort | null {
  return sort && roadmapColumns(showHours).includes(sort.column) ? sort : null;
}

export type SortDirection = "asc" | "desc";

/** A column sort; none (null) keeps the default rank. */
export interface RoadmapSort {
  column: RoadmapColumn;
  direction: SortDirection;
}

export function isRoadmapColumn(value: unknown): value is RoadmapColumn {
  return typeof value === "string" && (ROADMAP_COLUMNS as readonly string[]).includes(value);
}

/**
 * The sort after a click on a column's header: ascending, then descending,
 * then back to the default rank. Another column starts at ascending.
 */
export function nextSort(sort: RoadmapSort | null, column: RoadmapColumn): RoadmapSort | null {
  if (sort?.column !== column) {
    return { column, direction: "asc" };
  }
  return sort.direction === "asc" ? { column, direction: "desc" } : null;
}

/** A column header's `aria-sort`: only the sorted column carries one. */
export function ariaSort(
  sort: RoadmapSort | null,
  column: RoadmapColumn,
): "ascending" | "descending" | undefined {
  if (sort?.column !== column) {
    return undefined;
  }
  return sort.direction === "asc" ? "ascending" : "descending";
}

/** What the column sort reads of an item. */
export interface SortFields extends EffortFields {
  title: string;
  priority: string;
  month?: string | null;
  deadline?: string | null;
  followers: readonly string[];
}

/**
 * The value an item sorts on in a column; null for none, which sorts last
 * either way. Priority ascends from low to high, effort by the hours shown
 * (`itemEffort`), month and deadline by date.
 */
function sortValue(item: SortFields, column: RoadmapColumn): string | number | null {
  switch (column) {
    case "item":
      return item.title;
    case "priority": {
      const rank = priorityRank(item.priority);
      return rank < ROADMAP_PRIORITIES.length ? ROADMAP_PRIORITIES.length - rank : null;
    }
    case "effort":
      return itemEffort(item).hours;
    case "month":
      return item.month ?? null;
    case "deadline":
      return item.deadline ?? null;
    case "followers":
      return item.followers.length;
  }
}

/**
 * What a column's sort does, for its header's tooltip: its order, then
 * that a second click turns it round and a third goes back to the default
 * rank. "Sorteer op titel van A tot Z. Klik opnieuw voor Z tot A, en een
 * derde keer voor de standaardvolgorde."
 */
export function sortHint(column: RoadmapColumn): string {
  return i18n.t(`roadmap.sort.hint.${column}`);
}

/**
 * What the list is sorted on while a column sort is active, for the line
 * above the table: "Gesorteerd op inspanning, van klein naar groot."
 */
export function sortSummary(sort: RoadmapSort): string {
  return i18n.t("roadmap.sort.active", {
    column: i18n.t(`roadmap.sort.by.${sort.column}`),
    order: i18n.t(`roadmap.sort.${sort.direction}.${sort.column}`),
  });
}

/**
 * Items sorted on a column, or in the order they came (the default rank)
 * without a sort. Titles compare in the dashboard's language, ignoring case
 * and accents; items without a value go last in both directions; ties keep
 * the order they came in. Sort each group on its own: a sort never moves an
 * item to another group.
 */
export function sortItems<T extends SortFields>(
  items: readonly T[],
  sort: RoadmapSort | null,
  language: Language = getLanguage(),
): T[] {
  if (!sort) {
    return [...items];
  }
  const collator = new Intl.Collator(LOCALES[language], { sensitivity: "base", numeric: true });
  const sign = sort.direction === "asc" ? 1 : -1;
  return [...items].sort((a, b) => {
    const x = sortValue(a, sort.column);
    const y = sortValue(b, sort.column);
    if (x === null || y === null) {
      return x === y ? 0 : x === null ? 1 : -1;
    }
    const order =
      typeof x === "number" && typeof y === "number"
        ? x - y
        : collator.compare(String(x), String(y));
    return sign * order;
  });
}

// ─── Months ──────────────────────────────────────────────────────────────

/** This month in Belgian time, as the API writes months: "2026-10". */
export function currentMonth(now: Date = new Date()): string {
  return (belgianDay(now) ?? "").slice(0, 7);
}

/** A number of months from a month on, that month first: the choices to plan an item in. */
export function monthsFrom(month: string, count: number): string[] {
  return Array.from({ length: Math.max(0, count) }, (_, index) => shiftMonth(month, index));
}

/** The month block's last figure: what is left of the budget, or how far over it the month is. */
export interface LeftFigure {
  /** "Over", or "Boven budget" when the month holds more than its budget. */
  label: "left" | "over";
  /** Never negative: the hours left, or the hours over. */
  hours: number;
}

export function leftFigure(leftHours: number): LeftFigure {
  return leftHours < 0 ? { label: "over", hours: -leftHours } : { label: "left", hours: leftHours };
}

// ─── Filters, sort and month in the URL ──────────────────────────────────

/** The priority filter: every priority, or one. */
export type PriorityFilter = "all" | RoadmapPriority;

export const PRIORITY_FILTERS: readonly PriorityFilter[] = ["all", ...ROADMAP_PRIORITIES];

/** The filter bar's pressed toggles, in the bar's order. */
export const ROADMAP_TOGGLES = ["blocked", "waitingOnMe", "followedByMe"] as const;
export type RoadmapToggle = (typeof ROADMAP_TOGGLES)[number];

/** The Roadmap page's filters: a search, a priority and three toggles. */
export interface RoadmapFilters {
  search: string;
  priority: PriorityFilter;
  /** Only items with an open blockage. */
  blocked: boolean;
  /** Only items with an open blockage that names the viewer. */
  waitingOnMe: boolean;
  /** Only items the viewer follows. */
  followedByMe: boolean;
}

export const NO_ROADMAP_FILTERS: RoadmapFilters = {
  search: "",
  priority: "all",
  blocked: false,
  waitingOnMe: false,
  followedByMe: false,
};

/** Everything the page keeps in its URL: the filters, the column sort and the month on show. */
export interface RoadmapView {
  filters: RoadmapFilters;
  sort: RoadmapSort | null;
  /** The month block's month ("2026-11"); null for the current month. */
  month: string | null;
}

/** The view's keys in the page's URL, so a filtered view can be shared. */
const PARAM = {
  search: "q",
  priority: "priority",
  blocked: "blocked",
  waitingOnMe: "waiting",
  followedByMe: "following",
  sort: "sort",
  direction: "dir",
  month: "month",
} as const;

/** A toggle is on in the URL as "1"; anything else is off. */
const ON = "1";

/**
 * The filters in the page's URL
 * (`?q=offerte&priority=high&blocked=1&waiting=1&following=1`). A priority
 * it does not know counts as every priority.
 */
export function parseRoadmapFilters(params: URLSearchParams): RoadmapFilters {
  const priority = params.get(PARAM.priority);
  return {
    search: params.get(PARAM.search) ?? "",
    priority: isRoadmapPriority(priority) ? priority : "all",
    blocked: params.get(PARAM.blocked) === ON,
    waitingOnMe: params.get(PARAM.waitingOnMe) === ON,
    followedByMe: params.get(PARAM.followedByMe) === ON,
  };
}

/** The filters as URL parameters, leaving out every filter that is not set. */
export function roadmapFilterParams(filters: RoadmapFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters.search.trim() !== "") {
    params[PARAM.search] = filters.search;
  }
  if (filters.priority !== "all") {
    params[PARAM.priority] = filters.priority;
  }
  for (const toggle of ROADMAP_TOGGLES) {
    if (filters[toggle]) {
      params[PARAM[toggle]] = ON;
    }
  }
  return params;
}

/** Whether any filter is set: the page then offers to reset them. */
export function hasRoadmapFilters(filters: RoadmapFilters): boolean {
  return Object.keys(roadmapFilterParams(filters)).length > 0;
}

/**
 * The column sort in the URL (`?sort=effort&dir=desc`); null for a column
 * it does not know. A direction it does not know reads as ascending.
 */
export function parseRoadmapSort(params: URLSearchParams): RoadmapSort | null {
  const column = params.get(PARAM.sort);
  if (!isRoadmapColumn(column)) {
    return null;
  }
  return { column, direction: params.get(PARAM.direction) === "desc" ? "desc" : "asc" };
}

/** The month in the URL (`?month=2026-11`); null when there is none or it is no month. */
export function parseRoadmapMonth(params: URLSearchParams): string | null {
  const month = params.get(PARAM.month);
  return isMonth(month) ? month : null;
}

export function parseRoadmapView(params: URLSearchParams): RoadmapView {
  return {
    filters: parseRoadmapFilters(params),
    sort: parseRoadmapSort(params),
    month: parseRoadmapMonth(params),
  };
}

/** The whole view as URL parameters, leaving out what is not set. */
export function roadmapViewParams(view: RoadmapView): Record<string, string> {
  const params = roadmapFilterParams(view.filters);
  if (view.sort) {
    params[PARAM.sort] = view.sort.column;
    params[PARAM.direction] = view.sort.direction;
  }
  if (view.month && isMonth(view.month)) {
    params[PARAM.month] = view.month;
  }
  return params;
}

// ─── Matching the filters ────────────────────────────────────────────────

/** What the filters read of an item. Only a developer's items carry `developer`. */
export interface FilterFields {
  status: string;
  title: string;
  summary?: string | null;
  priority: string;
  blocked: boolean;
  blockers: readonly OpenFields[];
  followers: readonly string[];
  developer?: { linear: readonly { id: string }[] } | null;
}

/**
 * The texts the search looks in: the title and the summary, and for a
 * developer, whose items carry them, the Linear ids.
 */
export function roadmapSearchTexts(item: FilterFields): string[] {
  return [
    item.title,
    item.summary ?? "",
    ...(item.developer?.linear ?? []).map((ticket) => ticket.id),
  ];
}

/**
 * Whether an item has what a toggle asks for, for the viewer (their email):
 * an open blockage (the server's `blocked`), an open blockage that names the
 * viewer, or the viewer among its followers. A done item waits on nobody, as
 * the server's `blocked` and the table's lines (`blockageLines`) have it,
 * even when a blockage of it was never resolved.
 */
export function hasToggle(item: FilterFields, toggle: RoadmapToggle, viewer: string): boolean {
  switch (toggle) {
    case "blocked":
      return item.blocked;
    case "waitingOnMe":
      return (
        item.status !== "done" &&
        item.blockers.some((blocker) => isOpenBlocker(blocker) && namesPerson(blocker, viewer))
      );
    case "followedByMe": {
      const me = normalEmail(viewer);
      return item.followers.some((email) => normalEmail(email) === me);
    }
  }
}

/**
 * Whether an item matches the filters for the viewer: every word of the
 * search in its texts (`roadmapSearchTexts`), its priority, and every toggle
 * that is on.
 */
export function matchesRoadmapFilters(
  item: FilterFields,
  filters: RoadmapFilters,
  viewer: string,
): boolean {
  if (filters.priority !== "all" && item.priority !== filters.priority) {
    return false;
  }
  if (ROADMAP_TOGGLES.some((toggle) => filters[toggle] && !hasToggle(item, toggle, viewer))) {
    return false;
  }
  return matchesSearch(roadmapSearchTexts(item), filters.search);
}

/**
 * The count on each toggle: how many items it would show with the other
 * filters as they are.
 */
export function toggleCounts(
  items: readonly FilterFields[],
  filters: RoadmapFilters,
  viewer: string,
): Record<RoadmapToggle, number> {
  const count = (toggle: RoadmapToggle) =>
    items.filter((item) => matchesRoadmapFilters(item, { ...filters, [toggle]: true }, viewer))
      .length;
  return {
    blocked: count("blocked"),
    waitingOnMe: count("waitingOnMe"),
    followedByMe: count("followedByMe"),
  };
}

// ─── Blockages ───────────────────────────────────────────────────────────

/** What telling an open blockage from a resolved one reads of it. */
export interface OpenFields {
  /** The emails it names, for a blockage of people; empty for any other. */
  people: readonly string[];
  /**
   * When it was resolved, or the item it names was done (the server reads
   * that at once); null while it is open.
   */
  resolved_at?: string | null;
}

/** Whether a blockage is open: nobody resolved it, and the item it names is not done. */
export function isOpenBlocker(blocker: { resolved_at?: string | null }): boolean {
  return !blocker.resolved_at;
}

/** Whether a blockage names this person (their email, any case). */
export function namesPerson(blocker: { people: readonly string[] }, email: string): boolean {
  const me = normalEmail(email);
  return blocker.people.some((person) => normalEmail(person) === me);
}

/** What a blockage's line reads of it. */
export interface BlockerFields extends OpenFields {
  id: string;
  kind: string;
  item_id?: string | null;
  party?: string | null;
  ask?: string | null;
}

/** What a blockage's line reads of the page: the people, the items and the viewer. */
export interface BlockageContext {
  /** The dashboard's accounts, by email in lower case. */
  people: ReadonlyMap<string, NamedPerson>;
  /** The roadmap's items, by id. */
  items: ReadonlyMap<string, { title: string; status: string }>;
  /** The viewer's email. */
  viewer: string;
}

/** One blockage as a line: who, then what they need to do. */
export interface BlockageLine {
  id: string;
  kind: RoadmapBlockerKind;
  /**
   * Who it waits on: the people by first name (`firstNameLabel`: "An en
   * Bram"); the free text of one of another kind ("Een leverancier"); the
   * item's title.
   */
  who: string;
  /** What they need to do; null when the blockage leaves it out (an item may). */
  ask: string | null;
  /** It names the viewer: the page emphasises it. */
  mine: boolean;
  /** For a blockage of an item: the item waited on, for a link to its details. */
  item?: { id: string; status: string };
}

/**
 * A blockage as a line, in the dashboard's language: who it waits on, what
 * they need to do, and whether it names the viewer. Null for one that names
 * an item the page does not know, or a kind it does not know.
 */
export function blockageLine(
  blocker: BlockerFields,
  context: BlockageContext,
): BlockageLine | null {
  const ask = blocker.ask?.trim() || null;
  const base = { id: blocker.id, ask, mine: false };
  switch (blocker.kind) {
    case "people": {
      const name = firstNameLabel(context.people.values());
      const names = blocker.people.map((email) => {
        return name(context.people.get(normalEmail(email)) ?? { email });
      });
      return {
        ...base,
        kind: "people",
        who: formatList(names),
        mine: namesPerson(blocker, context.viewer),
      };
    }
    case "party":
      return blocker.party ? { ...base, kind: "party", who: blocker.party } : null;
    case "item": {
      const waited = blocker.item_id ? context.items.get(blocker.item_id) : undefined;
      return blocker.item_id && waited
        ? {
            ...base,
            kind: "item",
            who: waited.title,
            item: { id: blocker.item_id, status: waited.status },
          }
        : null;
    }
    default:
      return null;
  }
}

/** How many of an item's blockages the table lists; the rest are a "+2 andere blokkades". */
export const SHOWN_BLOCKAGES = 3;

/** What the table reads of an item for its blockages. */
export interface BlockageItemFields {
  status: string;
  blockers: readonly BlockerFields[];
}

/**
 * The open blockages under an item's summary in the table, as lines: at most
 * `shown` of them, those that name the viewer first and the rest in the
 * server's order (the oldest first), and how many more there are. A done item
 * waits on nothing, and a resolved blockage, or one whose item is done,
 * holds nothing up.
 */
export function blockageLines(
  item: BlockageItemFields,
  context: BlockageContext,
  shown = SHOWN_BLOCKAGES,
): { lines: BlockageLine[]; more: number } {
  if (item.status === "done") {
    return { lines: [], more: 0 };
  }
  const lines = item.blockers
    .filter(isOpenBlocker)
    .flatMap((blocker) => blockageLine(blocker, context) ?? []);
  const ordered = [...lines.filter((line) => line.mine), ...lines.filter((line) => !line.mine)];
  return { lines: ordered.slice(0, shown), more: Math.max(0, ordered.length - shown) };
}

// ─── Followers ───────────────────────────────────────────────────────────

/** How many followers' avatars a row stacks; those who followed earlier are a "+2". */
export const SHOWN_FOLLOWERS = 3;

/** A follower's avatar: their letters, and their first name for screen readers. */
export interface FollowerAvatar {
  email: string;
  initials: string;
  name: string;
}

/** An item's followers as the table stacks them. */
export interface FollowerStack {
  /**
   * The latest few followers' avatars, in the order they followed: the
   * newest comes last, so it lies on top of the stack.
   */
  avatars: FollowerAvatar[];
  /** How many followed before them: "+2" after the stack. */
  more: number;
  /**
   * Every follower in the order they followed, as the stack's tooltip names
   * them, by first name (`firstNameLabel`): "Davy, eigenaar", or their
   * email's part before the @ alone for someone without a dashboard account.
   */
  names: string[];
}

/**
 * The followers' stack: the avatars of the latest `shown` of them (the
 * server lists followers in the order they followed), how many came before,
 * and every follower's name with their role.
 */
export function followerStack(
  followers: readonly string[],
  people: ReadonlyMap<string, NamedPerson & { role?: string | null }>,
  shown = SHOWN_FOLLOWERS,
  language: Language = getLanguage(),
): FollowerStack {
  const known = (email: string) => people.get(normalEmail(email)) ?? { email };
  const label = firstNameLabel(people.values());
  return {
    avatars: followers.slice(Math.max(0, followers.length - shown)).map((email) => {
      const person = known(email);
      return { email, initials: personInitials(person), name: label(person) };
    }),
    more: Math.max(0, followers.length - shown),
    names: followers.map((email) => {
      const person = known(email);
      const name = label(person);
      return "role" in person && person.role
        ? i18n.t("roadmap.followers.person", {
            name,
            role: roleLabel(person.role).toLocaleLowerCase(LOCALES[language]),
          })
        : name;
    }),
  };
}

/**
 * What to say when a follow or an unfollow failed. Every role on the roadmap
 * may follow an item that is not done, so the server refuses one
 * (`roadmap_forbidden`) only when the item was finished in the meantime: say
 * that, never that the viewer's role falls short. Anything else as
 * `apiErrorMessage` words it.
 */
export function followFailureMessage(error: unknown): string {
  return apiErrorCode(error) === "roadmap_forbidden"
    ? i18n.t("roadmap.followers.closed")
    : apiErrorMessage(error, "followRoadmapItem");
}

// ─── The lock note ───────────────────────────────────────────────────────

/** What the lock note reads of what the viewer may do with an item (the server's `can`). */
export interface LockRights {
  edit_text: boolean;
  set_effort: boolean;
}

/** Why an item is locked for the viewer, as a text key (`lockNote`). */
export type LockNote =
  "roadmap.details.lockedInProgress" | "roadmap.details.lockedDone" | "roadmap.details.lockedPast";

/**
 * Why the viewer may not change an item, as a text key: it is done and they
 * may not change it at all, a past month is on show (`pastMonth`), where
 * nothing changes, or it is in progress and they may not edit it. A
 * developer still sets a done item's actual hours and month, and an item's
 * effort and month from a past month too (`set_effort`), so neither is
 * locked for them. Null when nothing is locked.
 */
export function lockNote(status: string, can: LockRights, pastMonth = false): LockNote | null {
  if (status === "done") {
    return can.set_effort ? null : "roadmap.details.lockedDone";
  }
  if (pastMonth) {
    return can.set_effort ? null : "roadmap.details.lockedPast";
  }
  return status === "in_progress" && !can.edit_text ? "roadmap.details.lockedInProgress" : null;
}

// ─── Moving an item ──────────────────────────────────────────────────────

/** A move to another status, by its text's key in the details. */
export type StatusMove = "plan" | "start" | "finish" | "unplan" | "unstart";

/** A move the status menu offers. */
export interface StatusMoveOption {
  move: StatusMove;
  /**
   * What to send for it at once; null for Markeer klaar, which asks for the
   * actual hours first.
   */
  change: RoadmapStatusChange | null;
  /** Inplannen waits for an effort: the menu shows it disabled, with this reason. */
  blocked: "roadmap.statusMenu.needsEffort" | null;
}

/** What the status menu reads of an item. */
export interface MoveFields {
  /** Whether it has an effort, for every viewer (an admin gets no hours). */
  estimated: boolean;
  can: Pick<RoadmapPermissions, StatusMove>;
}

/**
 * The status moves the server allows the viewer on an item (its `can`), in
 * the order work goes and as the details list them: forward first
 * (Inplannen, Start, Markeer klaar), then back (Terug naar ideeën, Terug naar
 * gepland). Inplannen plans in `month`, and is blocked while the item has no
 * effort (`estimated` false), which every viewer knows, an admin too.
 */
export function statusMoves(item: MoveFields, month: string): StatusMoveOption[] {
  const changes: Record<StatusMove, RoadmapStatusChange | null> = {
    plan: { status: "planned", month },
    start: { status: "in_progress" },
    finish: null,
    unplan: { status: "idea" },
    unstart: { status: "planned" },
  };
  const order: StatusMove[] = ["plan", "start", "finish", "unplan", "unstart"];
  return order
    .filter((move) => item.can[move])
    .map((move) => ({
      move,
      change: changes[move],
      blocked: move === "plan" && !item.estimated ? "roadmap.statusMenu.needsEffort" : null,
    }));
}

// ─── Editing an item ─────────────────────────────────────────────────────

/** The longest title and summary the server takes. */
export const TITLE_MAX = 200;
export const SUMMARY_MAX = 2000;

/**
 * What a developer's idea form holds: "Nieuw idee", and "Naar backlog" on a
 * triage entry.
 */
export interface IdeaDraft {
  title: string;
  summary: string;
  priority: RoadmapPriority;
  /** The Linear ids as typed: one or more, separated by commas or spaces. */
  linear: string;
  /** The effort chosen, one of the steps in hours; null while to be estimated. */
  effort: number | null;
  /**
   * The language the title and summary are written in, as Naar backlog's
   * select holds it. Left out for Nieuw idee: the dashboard's language when
   * it is sent.
   */
  language?: Language;
}

/** Why the Linear ids as typed cannot be sent. */
export type LinearProblem =
  { kind: "required" } | { kind: "invalid"; ids: string[] } | { kind: "tooMany" };

/**
 * The Linear ids typed in one field, separated by commas, semicolons or white
 * space (a pasted list too): each in upper case and once, in the order typed,
 * and apart from them what is no Linear id, as typed.
 */
export function parseLinearIds(text: string): { ids: string[]; invalid: string[] } {
  const ids: string[] = [];
  const invalid: string[] = [];
  for (const part of text.split(/[\s,;]+/)) {
    if (part === "") {
      continue;
    }
    const id = normalLinearId(part);
    if (!id) {
      invalid.push(part);
    } else if (!ids.includes(id)) {
      ids.push(id);
    }
  }
  return { ids, invalid };
}

/** What is wrong with the Linear ids as typed: none, something that is no id, or too many. */
export function linearProblem(text: string): LinearProblem | null {
  const { ids, invalid } = parseLinearIds(text);
  if (invalid.length > 0) {
    return { kind: "invalid", ids: invalid };
  }
  if (ids.length === 0) {
    return { kind: "required" };
  }
  return ids.length > MAX_LINEAR_IDS ? { kind: "tooMany" } : null;
}

/** A problem with the Linear ids, in the dashboard's language. */
export function linearProblemMessage(problem: LinearProblem): string {
  switch (problem.kind) {
    case "required":
      return i18n.t("roadmap.form.linearRequired");
    case "invalid":
      return i18n.t("roadmap.form.linearInvalid", {
        count: problem.ids.length,
        ids: formatList(problem.ids),
      });
    case "tooMany":
      return i18n.t("roadmap.form.linearTooMany", { max: MAX_LINEAR_IDS });
  }
}

/**
 * The new idea to send for a developer's draft, the texts trimmed: with its
 * Linear ids, and its effort when one is chosen. It carries the language the
 * idea is written in, which the item keeps: the draft's, else the dashboard's
 * at this moment. Null while the title or the summary is empty or the Linear
 * ids have a problem (`linearProblem`).
 */
export function ideaCreate(draft: IdeaDraft): RoadmapItemCreate | null {
  const title = draft.title.trim();
  const summary = draft.summary.trim();
  if (title === "" || summary === "" || linearProblem(draft.linear)) {
    return null;
  }
  return {
    title,
    summary,
    priority: draft.priority,
    language: draft.language ?? getLanguage(),
    linear_ids: parseLinearIds(draft.linear).ids,
    ...(draft.effort === null ? {} : { effort_hours: draft.effort }),
  };
}

/** What "Idee voorstellen" holds: a priority only when the person picks one. */
export interface ProposalDraft {
  title: string;
  summary: string;
  priority: RoadmapPriority | null;
}

/**
 * The idea to propose for a draft, the texts trimmed, in the dashboard's
 * language at this moment, the one it was written in: the developer who puts
 * it on the roadmap starts from it. Null while the title or the summary is
 * empty.
 */
export function ideaProposal(draft: ProposalDraft): RoadmapIdeaProposal | null {
  const title = draft.title.trim();
  const summary = draft.summary.trim();
  if (title === "" || summary === "") {
    return null;
  }
  return {
    title,
    summary,
    language: getLanguage(),
    ...(draft.priority ? { priority: draft.priority } : {}),
  };
}

/** What the edit form holds of an item. */
export interface ItemDraft {
  title: string;
  summary: string;
  priority: RoadmapPriority;
  /** A calendar day ("2026-11-15"), or null for none. */
  deadline: string | null;
}

/** What the edit form reads of an item. */
export interface DraftFields {
  title: string;
  summary?: string | null;
  priority: string;
  deadline?: string | null;
}

/**
 * An item as the edit form shows it: the item as it is now, with the fields
 * the viewer changed since the form opened (`edits`) on top. A field nobody
 * touched follows the item, so a change saved elsewhere meanwhile shows in
 * the form and is never sent back at its old value.
 */
export function itemDraft(item: DraftFields, edits: Partial<ItemDraft> = {}): ItemDraft {
  return {
    title: item.title,
    summary: item.summary ?? "",
    priority: isRoadmapPriority(item.priority) ? item.priority : "medium",
    deadline: item.deadline ?? null,
    ...edits,
  };
}

/** The pencil's name and the editing form's description, as text keys. */
export interface ContentEditKeys {
  pencil:
    "roadmap.details.editContent" | "roadmap.details.editText" | "roadmap.details.editPriority";
  editing:
    | "roadmap.details.editingContent"
    | "roadmap.details.editingText"
    | "roadmap.details.editingPriority";
}

/**
 * What the header's pencil edits, by what the viewer may change: the title,
 * the description and the priority, the title and the description alone, or
 * the priority alone (an admin on an item in progress). Null when nothing.
 */
export function contentEditKeys(can: EditRights): ContentEditKeys | null {
  if (can.edit_text && can.edit_priority) {
    return { pencil: "roadmap.details.editContent", editing: "roadmap.details.editingContent" };
  }
  if (can.edit_text) {
    return { pencil: "roadmap.details.editText", editing: "roadmap.details.editingText" };
  }
  if (can.edit_priority) {
    return { pencil: "roadmap.details.editPriority", editing: "roadmap.details.editingPriority" };
  }
  return null;
}

/** What the viewer may change of an item (the server's `can`). */
export interface EditRights {
  edit_text: boolean;
  edit_priority: boolean;
}

/** Whether two lists hold the same values, in any order. */
function sameSet(a: readonly string[], b: readonly string[]): boolean {
  const left = new Set(a);
  const right = new Set(b);
  return left.size === right.size && [...left].every((value) => right.has(value));
}

/**
 * The change to send for a draft: only the fields the viewer may change and
 * that differ from the item, the texts trimmed. Null when nothing changed.
 * What an item waits on changes through its blockages, never here.
 */
export function itemUpdate(
  item: DraftFields,
  draft: ItemDraft,
  can: EditRights,
): RoadmapItemUpdate | null {
  const change: RoadmapItemUpdate = {};
  if (can.edit_text) {
    const title = draft.title.trim();
    if (title !== item.title) {
      change.title = title;
    }
    const summary = draft.summary.trim();
    if (summary !== (item.summary ?? "")) {
      change.summary = summary;
    }
    if (draft.deadline !== (item.deadline ?? null)) {
      change.deadline = draft.deadline;
    }
  }
  if (can.edit_priority && draft.priority !== item.priority) {
    change.priority = draft.priority;
  }
  return Object.keys(change).length > 0 ? change : null;
}

/** What the item picker reads of an item. */
export interface WaitCandidateFields {
  id: string;
  status: string;
  blockers: readonly { item_id?: string | null; resolved_at?: string | null }[];
}

/** The items an item waits on now: those its open blockages name. */
function waitedItems(item: WaitCandidateFields): string[] {
  return item.blockers.flatMap((blocker) =>
    blocker.item_id && isOpenBlocker(blocker) ? [blocker.item_id] : [],
  );
}

/**
 * The items a new blockage of an item can name: every other item that is
 * not done, that the item does not wait on already, and that does not itself
 * wait on the item, directly or through other items (the server refuses such
 * a loop). Only open blockages count. In the order they came.
 */
export function waitCandidates<T extends WaitCandidateFields>(
  itemId: string,
  items: readonly T[],
): T[] {
  // Every item that waits on this one, directly or through others, this one included.
  const waiting = new Set([itemId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const item of items) {
      if (!waiting.has(item.id) && waitedItems(item).some((id) => waiting.has(id))) {
        waiting.add(item.id);
        grew = true;
      }
    }
  }
  const self = items.find((item) => item.id === itemId);
  const already = new Set(self ? waitedItems(self) : []);
  return items.filter(
    (item) => !waiting.has(item.id) && !already.has(item.id) && item.status !== "done",
  );
}

/**
 * The longest text the server takes for a blockage of another kind
 * ("Anders"), and for what it asks ("Wat"); the most people one blockage names.
 */
export const PARTY_MAX = 80;
export const ASK_MAX = 80;
export const MAX_BLOCKER_PEOPLE = 20;

/** What the form for a new blockage holds. */
export interface BlockerDraft {
  kind: RoadmapBlockerKind;
  /** Emails, lower case, in the order they were picked. */
  people: string[];
  /** The item to wait on. */
  itemId: string | null;
  /** For a blockage of another kind ("Anders"): who or what, in free text. */
  party: string;
  ask: string;
}

export const EMPTY_BLOCKER_DRAFT: BlockerDraft = {
  kind: "people",
  people: [],
  itemId: null,
  party: "",
  ask: "",
};

/**
 * The blockage to send for a draft, by its kind: the people with what they
 * need to do, the item with what needs to happen when that is said, or
 * another kind's free text with what needs to happen. The texts trimmed. Null while the draft
 * misses what the server needs (someone or something, and an ask unless it
 * is an item) or names more people than one blockage takes.
 */
export function blockerCreate(draft: BlockerDraft): RoadmapBlockerCreate | null {
  const ask = draft.ask.trim();
  switch (draft.kind) {
    case "people": {
      const named = draft.people.length > 0 && draft.people.length <= MAX_BLOCKER_PEOPLE;
      return named && ask !== "" ? { people: [...draft.people], ask } : null;
    }
    case "item":
      return draft.itemId ? { item_id: draft.itemId, ...(ask !== "" ? { ask } : {}) } : null;
    case "party": {
      const party = draft.party.trim();
      return party !== "" && ask !== "" ? { party, ask } : null;
    }
  }
}

/** A blockage's text the server found too long, by its field. */
type BlockerText = "ask" | "party";

/**
 * Which of a new blockage's texts a refusal says is too long: FastAPI's 422
 * lists each field it refused by its place ("body", "ask"). Null for any
 * other failure.
 */
export function tooLongBlockerText(error: unknown): BlockerText | null {
  const { status, body } = (error ?? {}) as { status?: unknown; body?: unknown };
  const detail =
    typeof body === "object" && body !== null ? (body as { detail?: unknown }).detail : null;
  if (status !== 422 || !Array.isArray(detail)) {
    return null;
  }
  for (const problem of detail as { loc?: unknown }[]) {
    const field = Array.isArray(problem?.loc) ? problem.loc.at(-1) : null;
    if (field === "ask" || field === "party") {
      return field;
    }
  }
  return null;
}

/**
 * What to say when adding a blockage failed: that "Wat" or the free text of
 * "Anders" is longer than the server takes (a 422 on that field), else as
 * `apiErrorMessage` words it.
 */
export function blockerFailureMessage(error: unknown): string {
  switch (tooLongBlockerText(error)) {
    case "ask":
      return i18n.t("roadmap.blockers.askTooLong", { max: formatNumber(ASK_MAX) });
    case "party":
      return i18n.t("roadmap.blockers.otherTooLong", { max: formatNumber(PARTY_MAX) });
    default:
      return apiErrorMessage(error, "addRoadmapBlocker");
  }
}

// ─── A developer's fields ────────────────────────────────────────────────

/** The most hours the server takes as actual hours or a budget. */
export const MAX_HOURS = 10_000;
/** The most Linear ids one item carries. */
export const MAX_LINEAR_IDS = 20;

/**
 * Whole hours as typed, 0 or more ("12"): an item's actual hours or a
 * month's budget. Null when the box is empty; undefined when it is no such
 * number.
 */
export function parseWholeHours(text: string): number | null | undefined {
  const clean = text.trim();
  if (clean === "") {
    return null;
  }
  if (!/^\d+$/.test(clean)) {
    return undefined;
  }
  const hours = Number(clean);
  return hours <= MAX_HOURS ? hours : undefined;
}

const LINEAR_ID = /^[A-Z][A-Z0-9]{0,9}-[0-9]{1,6}$/;

/** A Linear id as typed ("abc-12"), in upper case as the server keeps it; null for no id. */
export function normalLinearId(text: string): string | null {
  const id = text.trim().toUpperCase();
  return LINEAR_ID.test(id) ? id : null;
}

/** What the developer's part of the details holds of an item. */
export interface DeveloperDraft {
  /** The effort chosen, one of the steps in hours (`EFFORT_STEPS`); null while to be estimated. */
  effort: number | null;
  /** The actual whole hours, as typed. */
  actual: string;
  /** "2026-11"; null for an idea. */
  month: string | null;
  /** Linear ids, upper case. */
  linear: string[];
}

/** What the developer's part reads of an item. */
export interface DeveloperFields {
  status: string;
  effort_hours?: number | null;
  month?: string | null;
  actual_hours?: number | null;
  developer?: { linear: readonly { id: string }[] } | null;
}

/**
 * An item as the developer's part shows it: the item as it is now, with the
 * fields the developer changed since the form opened (`edits`) on top. A
 * field nobody touched follows the item, so a change saved elsewhere
 * meanwhile (in the other card, at Markeer klaar, by someone else) shows in
 * the form and is never sent back at its old value.
 */
export function developerDraft(
  item: DeveloperFields,
  edits: Partial<DeveloperDraft> = {},
): DeveloperDraft {
  const actual = item.actual_hours;
  return {
    effort: item.effort_hours ?? null,
    actual: actual === null || actual === undefined ? "" : String(actual),
    month: item.month ?? null,
    linear: (item.developer?.linear ?? []).map((ticket) => ticket.id),
    ...edits,
  };
}

/** A field of the developer's draft. */
export type DeveloperField = keyof DeveloperDraft;

/** What Planning shows and saves of the developer's fields. */
export const PLANNING_FIELDS: readonly DeveloperField[] = ["effort", "month"];
/** What the Ontwikkelaar card shows and saves. */
export const DEVELOPER_CARD_FIELDS: readonly DeveloperField[] = ["linear", "actual"];

/** Why the developer's draft cannot be saved, as a text key. */
export type DeveloperProblem =
  "roadmap.developer.estimateRequired" | "roadmap.developer.actualInvalid";

/**
 * The developer's change to send for a draft, by what the server takes: the
 * effort step and the Linear ids of an item not done (an item that is no
 * idea keeps an effort), the actual hours of a done item (empty: its effort
 * counts), the month of any item but an idea. Only the `fields` the form
 * shows, and only what differs from the item; null when nothing does. A
 * problem instead when the draft holds something the server would refuse.
 */
export function developerUpdate(
  item: DeveloperFields,
  draft: DeveloperDraft,
  fields: readonly DeveloperField[],
): { change: RoadmapDeveloperUpdate | null; problem: DeveloperProblem | null } {
  const shows = new Set(fields);
  const change: RoadmapDeveloperUpdate = {};
  const done = item.status === "done";
  if (!done) {
    if (shows.has("effort")) {
      if (draft.effort === null && item.status !== "idea") {
        return { change: null, problem: "roadmap.developer.estimateRequired" };
      }
      if (draft.effort !== (item.effort_hours ?? null)) {
        change.effort_hours = draft.effort;
      }
    }
    const linear = (item.developer?.linear ?? []).map((ticket) => ticket.id);
    if (shows.has("linear") && !sameSet(draft.linear, linear)) {
      change.linear_ids = [...draft.linear];
    }
  } else if (shows.has("actual")) {
    const actual = parseWholeHours(draft.actual);
    if (actual === undefined) {
      return { change: null, problem: "roadmap.developer.actualInvalid" };
    }
    if (actual !== (item.actual_hours ?? null)) {
      change.actual_hours = actual;
    }
  }
  if (shows.has("month") && item.status !== "idea" && draft.month && draft.month !== item.month) {
    change.month = draft.month;
  }
  return { change: Object.keys(change).length > 0 ? change : null, problem: null };
}

/** The Linear ids with one more at the end, unless it is no id, there already, or the list is full. */
export function withLinearId(linear: readonly string[], id: string | null): string[] {
  return id && !linear.includes(id) && linear.length < MAX_LINEAR_IDS
    ? [...linear, id]
    : [...linear];
}

/** What the Ontwikkelaar card's Opslaan does. */
export interface DeveloperCardSave {
  /** What to send; null when nothing changed. */
  change: RoadmapDeveloperUpdate | null;
  problem: DeveloperProblem | null;
  /** The add field holds text that is no Linear id: Opslaan says so rather than drop it. */
  typedInvalid: boolean;
}

/**
 * What the Ontwikkelaar card saves: its draft (`DEVELOPER_CARD_FIELDS`), with
 * an id typed in the add field but not yet added with + or Enter taken along.
 * Text there that is no Linear id stops the save, so it is never dropped
 * without a word. A done item has no add field.
 */
export function developerCardSave(
  item: DeveloperFields,
  draft: DeveloperDraft,
  typed: string,
): DeveloperCardSave {
  const text = item.status === "done" ? "" : typed.trim();
  const id = normalLinearId(text);
  if (text !== "" && !id) {
    return { change: null, problem: null, typedInvalid: true };
  }
  const linear = withLinearId(draft.linear, id);
  return {
    ...developerUpdate(item, { ...draft, linear }, DEVELOPER_CARD_FIELDS),
    typedInvalid: false,
  };
}

// ─── Choosing a month or a day ───────────────────────────────────────────

/**
 * A month as Inplannen names it: by its name alone in this year ("november"),
 * with its year in another ("januari 2027").
 */
export function planMonthLabel(month: string, now: string = currentMonth()): string {
  return month.slice(0, 4) === now.slice(0, 4) ? formatMonthName(month) : formatMonth(month);
}

/**
 * The months a month choice offers: `count` months from `from` on, and the
 * month chosen now (`include`) when it falls outside them, in order.
 */
export function monthOptions(from: string, count: number, include?: string | null): string[] {
  const months = monthsFrom(from, count);
  if (include && isMonth(include) && !months.includes(include)) {
    months.push(include);
    months.sort();
  }
  return months;
}
